import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenantProtection } from '../../../hooks/useTenantProtection';
import { Database, Loader } from 'lucide-react';

const EntityBrowser = ({ value, onChange }) => {
  const { user } = useAuth();
  const { getProtectedTenantCode } = useTenantProtection();
  
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);

  // Entity icon mapping (fallback to default if not found)
  const entityIcons = {
    'customer': '👥',
    'lead': '🎯',
    'opportunity': '💼',
    'activity': '📋',
    'salesnote': '📝',
    'contract': '📄',
    'user': '👤',
    'knowledge': '📚'
  };

  useEffect(() => {
    loadEntities();
  }, []);

  const loadEntities = async () => {
    try {
      setLoading(true);
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      const response = await fetch('/api/entity-metadata/available', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load entities');
      }

      const data = await response.json();
      
      // Map entities with icons
      const entitiesWithIcons = (data.data || []).map(entity => ({
        ...entity,
        icon: entityIcons[entity.id] || '📊'
      }));
      
      setEntities(entitiesWithIcons);
    } catch (error) {
      console.error('Error loading entities:', error);
      // Fallback to basic list if API fails
      setEntities([
        { id: 'customer', name: 'Customers', icon: '👥' },
        { id: 'lead', name: 'Leads', icon: '🎯' },
        { id: 'opportunity', name: 'Opportunities', icon: '💼' },
        { id: 'activity', name: 'Activities', icon: '📋' },
        { id: 'knowledge', name: 'Knowledge Articles', icon: '📚' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        <Database className="w-4 h-4 inline mr-2" />
        Select Entity
      </label>
      {loading ? (
        <div className="flex items-center justify-center py-2 text-gray-500 dark:text-gray-400">
          <Loader className="w-4 h-4 animate-spin mr-2" />
          Loading entities...
        </div>
      ) : (
        <>
          <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            disabled={loading}
          >
            <option value="">Choose an entity...</option>
            {entities.map(entity => (
              <option key={entity.id} value={entity.id}>
                {entity.icon} {entity.name || entity.displayName}
              </option>
            ))}
          </select>
          {value && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Data source: {entities.find(e => e.id === value)?.name || entities.find(e => e.id === value)?.displayName}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default EntityBrowser;

